import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Image,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  CalendarBlankIcon,
  CheckCircleIcon,
  ImageIcon,
  MapPinIcon,
  RobotIcon,
  UserIcon,
} from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import type { IDeliveryException } from "../../apis/failed-delivery"
import { AttributionBadge, ExceptionStatusBadge, ExceptionTypeBadge } from "./atoms"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { useFailedMutations } from "./hooks"
import { ReassignDialog } from "./ReassignDialog"

const ZERO_UUID = "00000000-0000-0000-0000-000000000000"

// We only have the raiser's UUID (no name in the payload). Surface the
// meaningful distinction — auto-detected vs manually logged — instead of a
// raw, useless id fragment.
const raisedSource = (userId: string | null | undefined, t: TFunction): { label: string; system: boolean } => {
  if (!userId || userId === ZERO_UUID) return { label: t("exDetail.raisedSystem"), system: true }
  return { label: t("exDetail.raisedManual"), system: false }
}

const ATTRIBUTION_VALUES = ["DRIVER_FAULT", "CUSTOMER_FAULT", "EXTERNAL", "SYSTEM_ERROR", "UNDETERMINED"]

// Spec §F-1: ESCALATED is the manager review stage — confirm attribution +
// reassign must be available there. OPEN/IN_PROGRESS are pre-escalation.
// RESOLVED/CLOSED are terminal — no actions.
const ACTIONABLE_STATUSES = ["OPEN", "IN_PROGRESS", "ESCALATED"]

// System-generated descriptions/notes are written by background jobs in older
// records — translate them on read so the manager UI stays localized even
// before the DB is backfilled.
const SYSTEM_TEXT_KEY: Record<string, string> = {
  "Auto-detected by background sweeper": "exDetail.sysAutoDetected",
  "Rating reminder sent to buyer":       "exDetail.sysRatingReminder",
}

function localizeSystemText(input: string | null | undefined, t: TFunction): string {
  if (!input) return ""
  // O112: the BE closes open exceptions on delivery with `system.delivery_exception.resolved_by_delivery`.
  if (input.startsWith("system.") || input.startsWith("error.")) return translateBackendError(input)
  const direct = SYSTEM_TEXT_KEY[input.trim()]
  if (direct) return t(direct)
  // "SLA breach (HIGH). Promised ship date passed." → translate severity inline.
  const slaMatch = input.match(/^SLA breach \(([A-Z]+)\)\. Promised ship date passed\.$/)
  if (slaMatch) {
    const sev = t(`exDetail.severity.${slaMatch[1]}`, { defaultValue: slaMatch[1] })
    return t("exDetail.slaBreach", { sev })
  }
  return input
}

interface Props {
  deliveryNoteId: string
  exception: IDeliveryException
}

export function ExceptionDetail({ deliveryNoteId, exception }: Props) {
  const { t } = useTranslation("delivery")
  const mutations = useFailedMutations()
  const { stateOf } = useDeliveryActions()
  const attributionOptions = ATTRIBUTION_VALUES.map((value) => ({
    value,
    label: t(`failed.attribution.${value}`),
  }))
  const [reassignOpened, { open: openReassign, close: closeReassign }] = useDisclosure(false)

  // Pre-fill the dropdown with the driver's draft so manager can just click
  // "Батлах" to accept, or override before confirming (spec §F-1: Q7).
  const [attribution, setAttribution] = useState<string | null>(exception.attributionDraft ?? null)
  const [resolutionNotes, setResolutionNotes] = useState("")

  useEffect(() => {
    setAttribution(exception.attributionDraft ?? null)
    setResolutionNotes("")
  }, [exception.id, exception.attributionDraft])

  const isActionable = ACTIONABLE_STATUSES.includes(exception.status)
  const attributionAlreadySet = !!exception.attribution
  const draftMatchesSelection =
    !!exception.attributionDraft && attribution === exception.attributionDraft

  const handleConfirmAttribution = async () => {
    if (!attribution) return
    await mutations.confirmAttribution(deliveryNoteId, exception.id, {
      attribution,
      resolutionNotes: resolutionNotes.trim() || null,
    })
    setAttribution(null)
    setResolutionNotes("")
  }

  const source = raisedSource(exception.raisedByUserId, t)
  const lat = exception.latitude ? Number(exception.latitude) : null
  const lng = exception.longitude ? Number(exception.longitude) : null
  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)

  const metaRows: Array<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = [
    {
      icon: <CalendarBlankIcon size={14} />,
      label: t("exDetail.registered"),
      value: dayjs(exception.raisedAt).format("YYYY-MM-DD HH:mm"),
    },
    {
      icon: source.system ? <RobotIcon size={14} /> : <UserIcon size={14} />,
      label: t("exDetail.source"),
      value: source.label,
    },
  ]
  if (hasCoords) {
    metaRows.push({
      icon: <MapPinIcon size={14} />,
      label: t("exDetail.location"),
      value: (
        <Anchor href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" size="sm" fw={500}>
          {t("exDetail.viewOnMap")}
        </Anchor>
      ),
    })
  }
  if (exception.resolvedAt) {
    metaRows.push({
      icon: <CheckCircleIcon size={14} weight="fill" color="var(--mantine-color-green-6)" />,
      label: t("exDetail.resolved"),
      value: dayjs(exception.resolvedAt).format("YYYY-MM-DD HH:mm"),
    })
  }

  const TextBlock = ({ label, value, tinted }: { label: string; value: string; tinted?: boolean }) => (
    <Box>
      <Text size="sm" c="dimmed" fw={600} tt="uppercase" mb={4}>
        {label}
      </Text>
      <Paper
        withBorder
        radius="sm"
        p="sm"
        bg={tinted ? "yellow.0" : undefined}
      >
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{value}</Text>
      </Paper>
    </Box>
  )

  const InfoColumn = (
    <Stack gap="md">
      {/* Header */}
      <Box>
        <Group gap="xs" mb="xs" wrap="wrap">
          <ExceptionTypeBadge type={exception.exceptionType} size="md" />
          <ExceptionStatusBadge status={exception.status} size="md" />
          <AttributionBadge attribution={exception.attribution} size="md" />
        </Group>
        <Text fw={700} size="lg" c="dark.7">
          {t("exDetail.attempt", { n: exception.attemptNumber })}
        </Text>
      </Box>

      {/* Meta info — bordered definition list with icons */}
      <Paper withBorder radius="md" p="sm">
        <Stack gap="xs">
          {metaRows.map((row) => (
            <Group key={row.label} gap="sm" wrap="nowrap">
              <ThemeIcon size={26} radius="md" variant="light" color="gray">
                {row.icon}
              </ThemeIcon>
              <Text size="sm" c="dimmed" miw={84}>
                {row.label}
              </Text>
              {typeof row.value === "string"
                ? <Text size="sm" fw={500}>{row.value}</Text>
                : row.value}
            </Group>
          ))}
        </Stack>
      </Paper>

      {/* Description / notes — old system rows are stored in English; translate
          on display until backend records are backfilled. */}
      {exception.description && (
        <TextBlock label={t("exDetail.descriptionLabel")} value={localizeSystemText(exception.description, t)} tinted />
      )}
      {exception.notes && (
        <TextBlock label={t("exDetail.notesLabel")} value={localizeSystemText(exception.notes, t)} />
      )}
      {exception.resolutionNotes && (
        <TextBlock label={t("exDetail.resolutionNotesLabel")} value={localizeSystemText(exception.resolutionNotes, t)} />
      )}

      {/* Photo grid */}
      <Box>
        <Group gap={6} mb="xs">
          <ImageIcon size={14} color="var(--mantine-color-gray-6)" />
          <Text size="sm" c="dimmed" fw={600} tt="uppercase">
            {t("exDetail.attachedPhotos")}{exception.photoUrls?.length ? ` (${exception.photoUrls.length})` : ""}
          </Text>
        </Group>
        {exception.photoUrls && exception.photoUrls.length > 0 ? (
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
            {exception.photoUrls.map((url, i) => (
              <Anchor key={i} href={url} target="_blank">
                <Image
                  src={url}
                  radius="sm"
                  h={84}
                  fit="cover"
                  fallbackSrc="/placeholder.png"
                />
              </Anchor>
            ))}
          </SimpleGrid>
        ) : (
          <Paper withBorder radius="sm" p="md" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">{t("exDetail.noPhotos")}</Text>
          </Paper>
        )}
      </Box>
    </Stack>
  )

  const ActionColumn = isActionable ? (
    <Card withBorder radius="md" p="md" bg="gray.0">
      <Stack gap="md">
        <Text size="sm" fw={700} c="dark.7">
          {t("exDetail.managerAction")}
        </Text>

        {mutations.error && (
          <Alert color="red" variant="light" py="xs">
            {translateBackendError(mutations.error)}
          </Alert>
        )}

        {/* Step 1 — confirm attribution */}
        {!attributionAlreadySet ? (
          <Box>
            <Group gap="xs" mb={6} wrap="nowrap">
              <Badge size="xs" variant="filled" color="brand" radius="xl">
                1
              </Badge>
              <Text size="sm" fw={600}>
                {t("exDetail.confirmAttribution")}
              </Text>
            </Group>

            <Stack gap="xs">
              <Select
                size="sm"
                label={t("exDetail.attributionLabel")}
                placeholder={t("exDetail.selectPlaceholder")}
                data={attributionOptions}
                value={attribution}
                onChange={setAttribution}
                clearable
                description={
                  exception.attributionDraft ? (
                    <Group gap={4} wrap="nowrap">
                      <Text component="span" size="sm" c="dimmed">
                        {t("exDetail.driverSuggestion")}
                      </Text>
                      <Text component="span" size="sm" fw={600}>
                        {attributionOptions.find(
                          (o) => o.value === exception.attributionDraft,
                        )?.label ?? exception.attributionDraft}
                      </Text>
                      {draftMatchesSelection && (
                        <CheckCircleIcon
                          size={12}
                          weight="fill"
                          color="var(--mantine-color-green-6)"
                        />
                      )}
                    </Group>
                  ) : undefined
                }
              />
              <Textarea
                size="sm"
                label={t("exDetail.noteLabel")}
                placeholder={t("exDetail.resolutionNotesPlaceholder")}
                minRows={2}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.currentTarget.value)}
              />
              <DeliveryActionButton
                state={stateOf("confirmAttribution", exception)}
                color="dark"
                onClick={handleConfirmAttribution}
                loading={mutations.submitting}
                disabled={!attribution}
              >
                {t("exDetail.confirmAttribution")}
              </DeliveryActionButton>
            </Stack>
          </Box>
        ) : (
          <Alert color="green" variant="light" py="xs">
            <Group gap="xs" wrap="nowrap">
              <CheckCircleIcon size={16} weight="fill" />
              <Text size="sm">{t("exDetail.attributionConfirmed")}</Text>
            </Group>
          </Alert>
        )}

        {!attributionAlreadySet && <Divider />}

        {/* Step 2 — reassign */}
        <Box>
          <Group gap="xs" mb={6} wrap="nowrap">
            <Badge
              size="xs"
              variant={attributionAlreadySet ? "filled" : "light"}
              color={attributionAlreadySet ? "brand" : "gray"}
              radius="xl"
            >
              2
            </Badge>
            <Text size="sm" fw={600}>
              {t("exDetail.reassign")}
            </Text>
          </Group>
          <Text size="sm" c="dimmed" mb="xs">
            {attributionAlreadySet
              ? t("exDetail.reassignHintSet")
              : t("exDetail.reassignHintPre")}
          </Text>
          <DeliveryActionButton
            state={stateOf("reassign", exception)}
            variant={attributionAlreadySet ? "filled" : "default"}
            color={attributionAlreadySet ? "dark" : undefined}
            onClick={openReassign}
            fullWidth
          >
            {t("exDetail.changeAssignment")}
          </DeliveryActionButton>
          {!stateOf("reassign", exception).allowed && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" mt={4}>
              {stateOf("reassign", exception).reason}
            </Text>
          )}
        </Box>
      </Stack>
    </Card>
  ) : null

  return (
    <>
      <Card withBorder radius="md" p="md">
        {ActionColumn ? (
          <Grid gap="lg">
            <Grid.Col span={{ base: 12, md: 7 }}>{InfoColumn}</Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>{ActionColumn}</Grid.Col>
          </Grid>
        ) : (
          InfoColumn
        )}
      </Card>

      <ReassignDialog
        opened={reassignOpened}
        onClose={closeReassign}
        deliveryNoteId={deliveryNoteId}
        previousExceptionId={exception.id}
      />
    </>
  )
}
